import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert } from '@mui/material';

import { ContentTypes, LinkTypes } from 'helpers';
import { isUrlLearningContent } from 'helpers/learningItemUtils';
import { SelectExistingContentFilters } from './SelectExistingContentFilters';
import { SelectExistingContentTable } from './SelectExistingContentTable';
import { fetchContentVersions, getContentPageInfo, selectContentById } from 'features/content/contentSlice';

const SelectContentDialog = ({
    openDialog,
    setOpenDialog,
    setFieldValue,
    setSorting,
    contentPayload,
    setContentPayload,
    sortBy,
    sortDirection,
    setSelectedContent,
    setSelectedPoster = null,
    contentType = null,
}) => {
    const dispatch = useDispatch();
    const content = useSelector(selectContentById);
    const contentPageInfo = useSelector(getContentPageInfo);
    const [searchStringState, setSearchStringState] = useState('');
    // Tracks whether the most-recently clicked Learning Item lacked a configured launch file.
    const [learningItemNotLaunchable, setLearningItemNotLaunchable] = useState(false);
    // Tracks whether the most-recently clicked item was the wrong content type (not a Learning item).
    const [wrongContentType, setWrongContentType] = useState(false);

    useEffect(() => {
        let contentTypeIds;
        if (contentType === 'image' || setSelectedPoster) {
            contentTypeIds = LinkTypes.Image.contentTypes.map((link) => link.id);
        } else if (contentType === 'video') {
            contentTypeIds = LinkTypes.Video.contentTypes.map((link) => link.id);
        } else if (contentType === 'pdf') {
            contentTypeIds = [ContentTypes.Pdf.id];
        } else if (contentType === 'audio') {
            contentTypeIds = [ContentTypes.Mp3.id];
        } else if (contentType === 'learning') {
            // Restrict to HTML and ZIP files — the two formats valid for learning items.
            contentTypeIds = [ContentTypes.Html.id, ContentTypes.Zip.id];
        } else {
            contentTypeIds = Object.values(ContentTypes)
                .filter((ct) => ct.id !== ContentTypes.Procedure.id && ct.id !== ContentTypes.Schoox.id)
                .map((ct) => ct.id);
        }

        setContentPayload({
            reset: true,
            startIndex: 0,
            stopIndex: 19,
            contentTypeIds,
        });
    }, []);

    return (
        <Dialog open={openDialog} fullWidth maxWidth="sm" onClose={() => setOpenDialog(false)}>
            <DialogTitle style={{ fontWeight: 700, fontSize: '24px' }}>Select Content</DialogTitle>
            <DialogContent style={{ padding: '10px 24px 20px' }}>
                {learningItemNotLaunchable && (
                    <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setLearningItemNotLaunchable(false)}>
                        This Learning Item does not have a launch file configured. Open it in Content Management and
                        select an HTML launch file before inserting it into a procedure.
                    </Alert>
                )}
                {wrongContentType && (
                    <Alert severity="error" sx={{ mb: 1 }} onClose={() => setWrongContentType(false)}>
                        Only Learning Items (zip or URL-based activities) can be selected here. Please choose a
                        content record that was uploaded as a Learning Item.
                    </Alert>
                )}
                <SelectExistingContentFilters
                    contentPayload={contentPayload}
                    setContentPayload={setContentPayload}
                    searchStringState={searchStringState}
                    setSearchStringState={setSearchStringState}
                    contentType={contentType}
                />
                <SelectExistingContentTable
                    content={
                        contentType === 'learning'
                            ? (content ?? []).filter(
                                  (item) => item?.default_link_type_id === LinkTypes.Learning.id
                              )
                            : content
                    }
                    pageInfo={contentPageInfo}
                    contentPayload={contentPayload}
                    sortDirection={sortDirection}
                    sortBy={sortBy}
                    handleSort={(_sortBy, _sortDirection) => setSorting(_sortBy, _sortDirection)}
                    handleRowClick={async (data) => {
                        if (contentType === 'learning') {
                            // Only allow Learning link-type records (default_link_type_id === 5).
                            // Other content types (e.g. PDF, CSV, video) must be excluded with a message.
                            if (data?.default_link_type_id !== LinkTypes.Learning.id) {
                                setLearningItemNotLaunchable(false);
                                setWrongContentType(true);
                                return;
                            }
                            setWrongContentType(false);
                            // For ZIP-based items a launch_file is required.
                            // URL-based items (isUrlLearningContent) never have a launch_file — allow them.
                            if (!data?.launch_file && !isUrlLearningContent(data)) {
                                setLearningItemNotLaunchable(true);
                                return;
                            }
                        }
                        setWrongContentType(false);
                        setLearningItemNotLaunchable(false);

                        const versions = await dispatch(fetchContentVersions(data?.content_id));
                        if (setSelectedPoster) {
                            setFieldValue('poster_image_url', data?.url);
                            setFieldValue('poster_id', data?.content_id);
                            setFieldValue('poster_image_name', data?.display_name);
                            setFieldValue('posterVersions', versions?.payload);
                            setSelectedPoster(data?.display_name);
                        } else {
                            setFieldValue('contentVersions', versions?.payload);
                            setSelectedContent(data?.display_name);
                            setFieldValue('content_id', data?.content_id);
                            setFieldValue('content_url', data?.url);
                        }
                        if (contentType === 'video' && data?.chapters && data?.chapters?.length > 0) {
                            setFieldValue('chapters', data.chapters);
                        }
                        setOpenDialog(false);
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};

export { SelectContentDialog };
